import { Type, type Static } from '@sinclair/typebox';

// Source unique des périodes des stats du site web — consommée par le schéma, validatePeriod
// et les @ApiQuery de StatsAppController (enum en ligne, jamais de $ref : le pont MCP de Pulse
// ne résout pas les références).
export const STATS_PERIOD_VALUES = ['today', '7d', '30d', '3mo', '6mo', '12mo'] as const;
export type StatsPeriodType = (typeof STATS_PERIOD_VALUES)[number];
export const StatsPeriod = Type.Union([
  Type.Literal('today'),
  Type.Literal('7d'),
  Type.Literal('30d'),
  Type.Literal('3mo'),
  Type.Literal('6mo'),
  Type.Literal('12mo'),
]);

// Périodes des statistiques d'activité : les mêmes + `week` (semaine civile en cours, lundi →
// dimanche dans le fuseau du pro — la seule fenêtre autorisée à contenir du futur, pour le widget
// « Cette semaine » de l'accueil).
export const BUSINESS_STATS_PERIOD_VALUES = ['today', 'week', '7d', '30d', '3mo', '6mo', '12mo'] as const;
export type BusinessStatsPeriodType = (typeof BUSINESS_STATS_PERIOD_VALUES)[number];
export const BusinessStatsPeriod = Type.Union([
  Type.Literal('today'),
  Type.Literal('week'),
  Type.Literal('7d'),
  Type.Literal('30d'),
  Type.Literal('3mo'),
  Type.Literal('6mo'),
  Type.Literal('12mo'),
]);

/** Clés de traduction (namespace `PROFESSIONAL_APP_STATISTICS_PAGE`) des libellés de période. */
export const STATS_PERIOD_LABEL_KEYS: Record<BusinessStatsPeriodType, string> = {
  today: 'STATS_PERIOD_TODAY',
  week: 'STATS_PERIOD_WEEK',
  '7d': 'STATS_PERIOD_7D',
  '30d': 'STATS_PERIOD_30D',
  '3mo': 'STATS_PERIOD_3MO',
  '6mo': 'STATS_PERIOD_6MO',
  '12mo': 'STATS_PERIOD_12MO',
};

export const WebsiteKpiSchema = Type.Object({
  visitors: Type.Number(),
  pageviews: Type.Number(),
  bounce_rate: Type.Union([Type.Number(), Type.Null()]),
  visit_duration: Type.Union([Type.Number(), Type.Null()]),
  gsc_clicks: Type.Number(),
  gsc_impressions: Type.Number(),
  gsc_ctr: Type.Union([Type.Number(), Type.Null()]),
  gsc_position: Type.Union([Type.Number(), Type.Null()]),
});
export type WebsiteKpi = Static<typeof WebsiteKpiSchema>;

export const WebsiteKpiWithTrendSchema = Type.Object({
  current: WebsiteKpiSchema,
  previous: WebsiteKpiSchema,
  trend: Type.Object({
    visitors: Type.Union([Type.Number(), Type.Null()]),
    pageviews: Type.Union([Type.Number(), Type.Null()]),
    bounce_rate: Type.Union([Type.Number(), Type.Null()]),
    visit_duration: Type.Union([Type.Number(), Type.Null()]),
    gsc_clicks: Type.Union([Type.Number(), Type.Null()]),
    gsc_impressions: Type.Union([Type.Number(), Type.Null()]),
    gsc_ctr: Type.Union([Type.Number(), Type.Null()]),
    gsc_position: Type.Union([Type.Number(), Type.Null()]),
  }),
});
export type WebsiteKpiWithTrend = Static<typeof WebsiteKpiWithTrendSchema>;

export const WebsiteTimeseriesPointSchema = Type.Object({
  date: Type.String(),
  visitors: Type.Number(),
  pageviews: Type.Number(),
});
export type WebsiteTimeseriesPoint = Static<typeof WebsiteTimeseriesPointSchema>;

export const WebsiteStatsOverviewResponseSchema = Type.Object({
  kpi: WebsiteKpiWithTrendSchema,
  timeseries: Type.Array(WebsiteTimeseriesPointSchema),
});
export type WebsiteStatsOverviewResponse = Static<typeof WebsiteStatsOverviewResponseSchema>;

export const WebsiteTopPagesResponseSchema = Type.Object({
  items: Type.Array(
    Type.Object({
      path: Type.String(),
      visitors: Type.Number(),
      pageviews: Type.Number(),
    }),
  ),
});
export type WebsiteTopPagesResponse = Static<typeof WebsiteTopPagesResponseSchema>;

export const WebsiteTopSourcesResponseSchema = Type.Object({
  items: Type.Array(
    Type.Object({
      source: Type.String(),
      visitors: Type.Number(),
    }),
  ),
});
export type WebsiteTopSourcesResponse = Static<typeof WebsiteTopSourcesResponseSchema>;

export const WebsiteGscQueriesResponseSchema = Type.Object({
  items: Type.Array(
    Type.Object({
      query: Type.String(),
      clicks: Type.Number(),
      impressions: Type.Number(),
      ctr: Type.Number(),
      position: Type.Number(),
    }),
  ),
});
export type WebsiteGscQueriesResponse = Static<typeof WebsiteGscQueriesResponseSchema>;

export const WebsiteGscPagesResponseSchema = Type.Object({
  items: Type.Array(
    Type.Object({
      page: Type.String(),
      clicks: Type.Number(),
      impressions: Type.Number(),
      ctr: Type.Number(),
      position: Type.Number(),
    }),
  ),
});
export type WebsiteGscPagesResponse = Static<typeof WebsiteGscPagesResponseSchema>;
